<script setup lang="ts">
/**
 * BeforeAfter — 「启用前 / 启用后」效果对比（Home #preview 区）
 * 同一个回放页面的两种状态切换：
 *  - before：比分、标题、评论、进度条/时长全部可见（剧透状态）
 *  - after ：标题→占位、比分→遮挡块、评论→占位、进度条/时长隐藏（守护状态）
 * 数据全部来自 i18n home.preview.*（含足球 + NBA 示例，虚构比分）。
 * 对比面板为纯 CSS mock 直出（不再请求后端 screenshots 静态图）。
 */
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import YfbIcon from '@/components/YfbIcon.vue'

const { t, tm } = useI18n()

const view = ref<'before' | 'after'>('after')

const match = computed<Record<string, any>>(() => {
  const r = tm('home.preview.mockPage.matches') as any
  const arr = r?.value ?? r
  return Array.isArray(arr) ? (arr[0] ?? {}) : {}
})

const comments = computed<string[]>(() => {
  const c = match.value?.comments
  return Array.isArray(c) ? (c as string[]) : []
})

const score = computed(() => `${match.value.homeScore ?? 0} - ${match.value.awayScore ?? 0}`)

</script>

<template>
  <div class="ba">
    <!-- 状态切换 -->
    <div class="ba__switch" role="tablist" :aria-label="t('home.preview.switchHint')">
      <button
        type="button"
        role="tab"
        class="ba__btn"
        :class="{ 'ba__btn--active': view === 'before' }"
        :aria-selected="view === 'before'"
        @click="view = 'before'"
      >
        {{ t('home.preview.beforeLabel') }}
      </button>
      <button
        type="button"
        role="tab"
        class="ba__btn"
        :class="{ 'ba__btn--active': view === 'after' }"
        :aria-selected="view === 'after'"
        @click="view = 'after'"
      >
        {{ t('home.preview.afterLabel') }}
      </button>
    </div>

    <!-- 对比面板：CSS mock 直出（不再请求后端截图） -->
    <div class="ba__pane">
      <!-- 页面顶栏：站点 + 状态徽章 -->
      <div class="ba__topbar">
        <span class="ba__site">{{ t('home.preview.mockPage.siteLabel') }}</span>
        <span
          class="ba__badge"
          :class="view === 'before' ? 'ba__badge--danger' : 'ba__badge--safe'"
        >
          <YfbIcon :name="view === 'before' ? 'ph:warning-bold' : 'ph:shield-check-bold'" :size="11" />
          {{ view === 'before' ? t('home.preview.mockPage.badgeSpoiler') : t('home.preview.mockPage.badgeProtected') }}
        </span>
      </div>

      <!-- 视频标题行 -->
      <div class="ba__title-row">
        <span class="ba__title" :class="{ 'ba__title--hidden': view === 'after' }">
          {{ view === 'before' ? match.title : t('home.preview.mockPage.placeholder.title') }}
        </span>
      </div>

      <!-- 播放器 -->
      <div class="player">
        <div class="player__topbar">
          <span class="player__play" aria-hidden="true">▶</span>
          <span class="player__league">{{ match.league }}</span>
          <span class="player__teams">
            <span>{{ match.homeTeam }}</span>
            <template v-if="view === 'before'">
              <span class="player__score">{{ score }}</span>
            </template>
            <template v-else>
              <span class="player__score player__score--hidden" aria-hidden="true">
                <i></i><i></i>
              </span>
            </template>
            <span>{{ match.awayTeam }}</span>
          </span>
        </div>

        <div class="player__viewport">
          <svg
            class="player__bg-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 2 L12 22 M2 12 L22 12" />
          </svg>
          <div v-if="view === 'after'" class="player__veil">
            <YfbIcon name="ph:eye-slash-bold" :size="18" />
            <span>{{ t('home.preview.afterHint') }}</span>
          </div>
        </div>

        <div class="player__bottombar">
          <span class="player__time">
            {{ view === 'before' ? '78:24 / 90:00' : '--:-- / --:--' }}
          </span>
          <span class="player__progress" aria-hidden="true">
            <i class="player__progress-fill" :class="{ 'player__progress-fill--hidden': view === 'after' }"></i>
          </span>
        </div>
      </div>

      <!-- 评论区 -->
      <div class="ba__comments">
        <template v-if="view === 'before'">
          <span v-for="c in comments" :key="c" class="ba__comment">{{ c }}</span>
        </template>
        <template v-else>
          <span v-for="i in 3" :key="i" class="ba__comment ba__comment--hidden">
            {{ t('home.preview.mockPage.placeholder.comment') }}
          </span>
        </template>
      </div>
    </div>

    <p class="ba__hint">
      {{ view === 'before' ? t('home.preview.beforeHint') : t('home.preview.afterHint') }}
      · {{ t('home.preview.switchHint') }}
    </p>
  </div>
</template>

<style scoped>
.ba {
  max-width: 1120px;
  margin: 0 auto;
}

/* 切换按钮 */
.ba__switch {
  display: flex;
  max-width: 420px;
  margin: 0 auto var(--yfb-sp-6);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-md);
  overflow: hidden;
  background-color: var(--yfb-surface);
}

.ba__btn {
  flex: 1;
  padding: var(--yfb-sp-3) var(--yfb-sp-4);
  background: transparent;
  border: none;
  color: var(--yfb-text-secondary);
  font-family: inherit;
  font-size: var(--yfb-fs-md);
  font-weight: 700;
  cursor: pointer;
  transition: background-color var(--yfb-anim-fast) ease-out, color var(--yfb-anim-fast) ease-out;
}

.ba__btn + .ba__btn {
  border-left: 1px solid var(--yfb-rule);
}

.ba__btn--active {
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
}

/* 面板容器 */
.ba__pane {
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-lg);
  background-color: var(--yfb-surface);
  box-shadow: var(--yfb-shadow-2);
  overflow: hidden;
}

/* 顶栏 */
.ba__topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--yfb-sp-3);
  padding: var(--yfb-sp-2) var(--yfb-sp-4);
  background-color: var(--yfb-surface-2);
  border-bottom: 1px solid var(--yfb-rule-soft);
}

.ba__site {
  font-family: var(--yfb-mono);
  font-size: var(--yfb-fs-xs);
  letter-spacing: var(--yfb-ls-mono);
  color: var(--yfb-text-secondary);
  text-transform: uppercase;
}

.ba__badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: var(--yfb-radius-sm);
  font-size: var(--yfb-fs-xs);
  font-weight: 700;
}

.ba__badge--danger {
  background-color: var(--yfb-signal-danger);
  color: var(--yfb-on-danger);
}

.ba__badge--safe {
  background-color: var(--yfb-signal-ok);
  color: var(--yfb-on-ok);
}

/* 标题行 */
.ba__title-row {
  padding: var(--yfb-sp-3) var(--yfb-sp-4);
  border-bottom: 1px solid var(--yfb-rule-soft);
}

.ba__title {
  font-size: var(--yfb-fs-md);
  font-weight: 600;
  color: var(--yfb-ink);
  line-height: var(--yfb-lh-body);
}

.ba__title--hidden {
  color: var(--yfb-accent);
  font-family: var(--yfb-mono);
  font-weight: 700;
  letter-spacing: var(--yfb-ls-mono);
}

/* 播放器 */
.player {
  background-color: var(--yfb-bg);
}

.player__topbar {
  display: flex;
  align-items: center;
  gap: var(--yfb-sp-3);
  padding: var(--yfb-sp-2) var(--yfb-sp-4);
  background-color: var(--yfb-surface-2);
  border-bottom: 1px solid var(--yfb-rule-soft);
}

.player__play {
  color: var(--yfb-accent);
  font-size: var(--yfb-fs-sm);
}

.player__league {
  font-family: var(--yfb-mono);
  font-size: var(--yfb-fs-xs);
  color: var(--yfb-text-secondary);
  letter-spacing: var(--yfb-ls-mono);
}

.player__teams {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--yfb-sp-3);
  font-size: var(--yfb-fs-md);
  font-weight: 700;
  color: var(--yfb-ink);
  min-width: 0;
  white-space: nowrap;
}

.player__score {
  padding: 1px 10px;
  background-color: var(--yfb-surface-2);
  border: 1px solid var(--yfb-accent);
  border-radius: var(--yfb-radius-sm);
  color: var(--yfb-accent);
  font-family: var(--yfb-mono);
  font-variant-numeric: tabular-nums;
}

.player__score--hidden {
  display: inline-flex;
  gap: 5px;
  border-color: var(--yfb-rule);
}

.player__score--hidden i {
  width: 16px;
  height: 12px;
  background-color: var(--yfb-accent);
  border-radius: 2px;
  box-shadow: 0 0 6px var(--yfb-glow-soft);
}

/* 画面区 */
.player__viewport {
  position: relative;
  aspect-ratio: 16 / 7;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--yfb-ink-soft);
}

.player__bg-icon {
  width: 64px;
  height: 64px;
  opacity: 0.35;
}

.player__veil {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--yfb-sp-2);
  background-color: rgba(11, 11, 16, 0.55);
  color: var(--yfb-accent);
  font-family: var(--yfb-mono);
  font-size: var(--yfb-fs-sm);
  font-weight: 700;
  letter-spacing: var(--yfb-ls-mono);
}

/* 底栏：时长 + 进度条 */
.player__bottombar {
  display: flex;
  align-items: center;
  gap: var(--yfb-sp-4);
  padding: var(--yfb-sp-2) var(--yfb-sp-4);
  background-color: var(--yfb-surface-2);
  border-top: 1px solid var(--yfb-rule-soft);
}

.player__time {
  flex-shrink: 0;
  font-family: var(--yfb-mono);
  font-size: var(--yfb-fs-xs);
  color: var(--yfb-text-secondary);
  font-variant-numeric: tabular-nums;
}

.player__progress {
  position: relative;
  flex: 1;
  height: 5px;
  background-color: var(--yfb-rule-soft);
  border-radius: 3px;
  overflow: hidden;
}

.player__progress-fill {
  position: absolute;
  inset: 0 auto 0 0;
  width: 78%;
  background-color: var(--yfb-accent);
  border-radius: 3px;
}

.player__progress-fill--hidden {
  background-color: transparent;
}

/* 评论区 */
.ba__comments {
  display: flex;
  flex-wrap: wrap;
  gap: var(--yfb-sp-2);
  padding: var(--yfb-sp-3) var(--yfb-sp-4);
  border-top: 1px solid var(--yfb-rule-soft);
}

.ba__comment {
  padding: 3px 10px;
  background-color: var(--yfb-surface-2);
  border: 1px solid var(--yfb-rule-soft);
  border-radius: var(--yfb-radius-sm);
  color: var(--yfb-ink);
  font-size: var(--yfb-fs-xs);
}

.ba__comment--hidden {
  color: var(--yfb-accent);
  font-family: var(--yfb-mono);
}

/* 提示行 */
.ba__hint {
  margin: var(--yfb-sp-4) 0 0;
  text-align: center;
  font-size: var(--yfb-fs-sm);
  color: var(--yfb-text-secondary);
}

/* 桌面端：面板放宽至 1120px，关键信息字号/间距随面板升级（移动端维持原样） */
@media (min-width: 768px) {
  .ba__topbar,
  .player__topbar,
  .player__bottombar,
  .ba__comments {
    padding: var(--yfb-sp-3) var(--yfb-sp-5);
  }

  .ba__title {
    font-size: var(--yfb-fs-lg);
  }

  .player__teams {
    font-size: var(--yfb-fs-lg);
  }

  .player__score {
    padding: 2px 14px;
    font-size: var(--yfb-fs-md);
  }

  .player__score--hidden i {
    width: 22px;
    height: 14px;
  }

  .player__bg-icon {
    width: 84px;
    height: 84px;
  }

  .player__veil {
    font-size: var(--yfb-fs-md);
  }

  .ba__comment {
    padding: 4px 12px;
    font-size: var(--yfb-fs-sm);
  }

  .player__time {
    font-size: var(--yfb-fs-sm);
  }

  .player__progress {
    height: 6px;
  }
}

@media (max-width: 767px) {
  .ba__switch {
    max-width: none;
  }

  .player__league {
    display: none;
  }

  .player__teams {
    font-size: var(--yfb-fs-sm);
  }
}
</style>
